import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, AlertCircle, Plus, Wallet, Users, CheckCircle,
  Infinity as InfinityIcon, X
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

// Reviewer management: work done, allowance left, and what is owed.
//
// Money here arrives already converted - the rate table stores CENTS per item,
// so summing it raw would overstate every figure by 100x.

const money = (v) =>
  v == null ? '—' : `€${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => (v == null ? '—' : v.toLocaleString());

const TYPE_LABELS = {
  final_human_image_revision: 'Final review',
  lens_manual_segmentation_verification: 'Segmentation',
};

function QuotaDialog({ user, onClose, onSaved, authenticatedFetch }) {
  const [amount, setAmount] = useState(500);
  const [absolute, setAbsolute] = useState(user.unlimited);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const res = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/users/${user.user_id}/quota`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: Number(amount), set_absolute: absolute }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      onSaved(data);
    } catch (err) { setError(err.message); setSaving(false); }
  };

  // Quota is a ceiling measured against work already done, so a top-up only
  // helps if it clears what they have used.
  const projected = absolute
    ? Math.max(0, Number(amount) || 0)
    : Math.max(0, (user.quota || 0) + (Number(amount) || 0));
  const projectedRemaining = Math.max(0, projected - user.quota_used);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Add reviewable images</h3>
            <p className="text-xs text-gray-500 mt-0.5">{user.name || user.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full">
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {user.unlimited && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
              This user currently has no limit. Setting a number here gives them a finite quota.
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {absolute ? 'Set total quota to' : 'Images to add'}
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            {!user.unlimited && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[100, 500, 1000, 2500].map((n) => (
                  <button key={n} onClick={() => { setAbsolute(false); setAmount(n); }}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">
                    +{n}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!user.unlimited && (
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" checked={absolute} onChange={(e) => setAbsolute(e.target.checked)} />
              Set the total instead of adding to it
            </label>
          )}

          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700 space-y-0.5">
            <div>Already used: <span className="font-medium">{num(user.quota_used)}</span></div>
            <div>New total quota: <span className="font-medium">{num(projected)}</span></div>
            <div>They will be able to review: <span className="font-semibold text-gray-900">{num(projectedRemaining)}</span> more</div>
            {projectedRemaining === 0 && (
              <div className="text-amber-800 mt-1">
                That is at or below what they have already done, so it frees nothing.
              </div>
            )}
          </div>

          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-900">{error}</div>}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
          <button onClick={onClose} disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-40 flex items-center justify-center gap-2">
            {saving ? <><RefreshCw className="w-4 h-4 animate-spin" />Saving…</> : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PayoutDialog({ user, onClose, onPaid, authenticatedFetch }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const res = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/users/${user.user_id}/payout`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      onPaid(data);
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Settle payout</h3>
          <p className="text-xs text-gray-500 mt-0.5">{user.name || user.email}</p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="p-4 bg-gray-50 border border-gray-200 rounded text-center">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Outstanding</div>
            <div className="mt-1 text-3xl font-semibold text-gray-900">{money(user.unpaid)}</div>
            <div className="mt-1 text-xs text-gray-500">{num(user.reviewed)} items reviewed</div>
          </div>
          <p className="text-xs text-gray-600">
            This marks their outstanding ledger entries as paid. It records that a payment
            was made — it does not move any money.
          </p>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-900">{error}</div>}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
          <button onClick={onClose} disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !user.unpaid}
            className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-40 flex items-center justify-center gap-2">
            {saving ? <><RefreshCw className="w-4 h-4 animate-spin" />Recording…</> : `Mark ${money(user.unpaid)} paid`}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserManagement() {
  const { authenticatedFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quotaUser, setQuotaUser] = useState(null);
  const [payoutUser, setPayoutUser] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await authenticatedFetch(`${config.BACKEND_API_ADDRESS}/users/management/overview`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
      setData(body);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message);
    } finally { setLoading(false); }
  }, [authenticatedFetch]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const users = data?.users || [];
  const totals = data?.totals;
  const active = users.filter((u) => u.reviewed > 0 || u.quota != null);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reviewers</h1>
          <p className="text-sm text-gray-600 mt-1">
            Work completed, remaining allowance, and outstanding payout.
          </p>
        </div>
        <button onClick={fetchOverview} disabled={loading}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">{error}</div>
        </div>
      )}

      {loading && !data ? (
        <div className="py-20 flex flex-col items-center gap-3 text-gray-500">
          <RefreshCw className="w-8 h-8 animate-spin" />
          <span className="text-sm">Loading reviewers…</span>
        </div>
      ) : !totals ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Reviewers</span>
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">{num(totals.reviewers)}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" style={{ color: '#0ca30c' }} />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Images reviewed</span>
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">{num(totals.reviewed)}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Outstanding</span>
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">{money(totals.unpaid)}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Already paid</span>
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">{money(totals.paid)}</div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">Reviewer</th>
                    <th className="text-right px-3 py-2.5 font-medium">Reviewed</th>
                    <th className="text-left px-3 py-2.5 font-medium">Breakdown</th>
                    <th className="text-right px-3 py-2.5 font-medium">Allowance left</th>
                    <th className="text-right px-3 py-2.5 font-medium">Owed</th>
                    <th className="px-5 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {active.map((u) => (
                    <tr key={u.user_id} className="hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <div className="font-medium text-gray-900">{u.name || '—'}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium text-gray-900">
                        {num(u.reviewed)}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-0.5">
                          {Object.entries(u.by_type).map(([k, v]) => (
                            <span key={k} className="text-xs text-gray-600">
                              {TYPE_LABELS[k] || k}: <span className="tabular-nums">{num(v)}</span>
                            </span>
                          ))}
                          {Object.keys(u.by_type).length === 0 && <span className="text-xs text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        {u.unlimited ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                            <InfinityIcon className="w-3.5 h-3.5" /> unlimited
                          </span>
                        ) : (
                          <div>
                            <div className={`tabular-nums font-medium ${u.remaining === 0 ? 'text-amber-700' : 'text-gray-900'}`}>
                              {num(u.remaining)}
                            </div>
                            <div className="text-[11px] text-gray-500 tabular-nums">
                              {num(u.quota_used)} / {num(u.quota)} used
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium text-gray-900">
                        {money(u.unpaid)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setQuotaUser(u)}
                            className="px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5">
                            <Plus className="w-3.5 h-3.5" />
                            Add images
                          </button>
                          <button onClick={() => setPayoutUser(u)} disabled={!u.unpaid}
                            className="px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1.5">
                            <Wallet className="w-3.5 h-3.5" />
                            Pay out
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {data.rates?.length > 0 && (
            <div className="mt-3 text-xs text-gray-500">
              Rates:{' '}
              {data.rates.map((r, i) => (
                <span key={r.identifier}>
                  {i > 0 && ' · '}
                  {TYPE_LABELS[r.identifier] || r.name} {money(r.per_item)}/image
                </span>
              ))}
              . Allowance is counted from completed review locks, which is what the
              reviewer's own limit is checked against; the reviewed total comes from the
              work ledger and can be higher, since one review can register several items.
            </div>
          )}
        </>
      )}

      {quotaUser && (
        <QuotaDialog
          user={quotaUser}
          authenticatedFetch={authenticatedFetch}
          onClose={() => setQuotaUser(null)}
          onSaved={async (res) => {
            setQuotaUser(null);
            setToast(`${res.email}: quota now ${num(res.quota)} — ${num(res.remaining)} available`);
            await fetchOverview();
          }}
        />
      )}

      {payoutUser && (
        <PayoutDialog
          user={payoutUser}
          authenticatedFetch={authenticatedFetch}
          onClose={() => setPayoutUser(null)}
          onPaid={async (res) => {
            setPayoutUser(null);
            setToast(`Recorded ${money(res.amount)} paid to ${res.email} (${num(res.settled)} items)`);
            await fetchOverview();
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-[80] bg-gray-900 text-white text-sm rounded-lg shadow-lg px-4 py-3 flex items-center gap-3">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{toast}</span>
          <button onClick={() => setToast(null)} className="opacity-70 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
